import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Divider, LinearProgress, Stack, TextField } from '@mui/material';
import { Button, Typography } from '@brainpool/components';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from '@clerk/clerk-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type ApplicationStatus = 'pending' | 'approved' | 'rejected';

type Profile = {
  id: number;
  clerk_user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  linkedin_url: string | null;
  linkedin_connected: number;
  linkedin_data_json: string | null;
  application_status: ApplicationStatus | null;
  applied_at: string | null;
  profile_picture_url: string | null;
  // null = never checked, 0 = non-EEA, 1 = EEA
  linkedin_eea_eligible: number | null;
};

type CvUpload = {
  id: number;
  original_filename: string;
  size_bytes: number;
  is_primary: number;
  uploaded_at: string;
};

type ClerkUser = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  primaryEmailAddress?: { emailAddress: string } | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = '.pdf,.doc,.docx';
const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

// ─── Main component ───────────────────────────────────────────────────────────

export function HomePage() {
  const { user, isLoaded } = useUser();
  const apiBaseUrl = import.meta.env.VITE_API_URL as string;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [existingCv, setExistingCv] = useState<CvUpload | null>(null);
  const [step, setStep] = useState(0);

  // LinkedIn
  const [isLinkedinConnected, setIsLinkedinConnected] = useState(false);
  const [linkedinNotice, setLinkedinNotice] = useState<'connected' | 'error' | 'not_eea' | null>(null);
  const [isConnectingLinkedIn, setIsConnectingLinkedIn] = useState(false);

  // Editable profile form (step 2 — user reviews/completes before submitting)
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    linkedinUrl: '',
  });

  // CV
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSavingCv, setIsSavingCv] = useState(false);
  const [cvSaveError, setCvSaveError] = useState('');

  // Apply
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  // ── Detect LinkedIn OAuth callback result from URL params ──────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linkedinParam = params.get('linkedin');
    if (!linkedinParam) return;

    if (linkedinParam === 'connected') {
      setLinkedinNotice('connected');
      setIsLinkedinConnected(true);
    } else {
      const linkedinError = params.get('linkedin_error');
      if (linkedinError === 'member_access_denied') {
        setLinkedinNotice('not_eea');
        // Stay on step 0 — show the LinkedIn button as disabled with error message.
      } else {
        setLinkedinNotice('error');
      }
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // ── Load profile ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !user?.id) return;

    const load = async () => {
      console.log('[HomePage] loading profile for user', user.id, 'from', apiBaseUrl);
      try {
        const res = await fetch(
          `${apiBaseUrl}/profile?clerkUserId=${encodeURIComponent(user.id)}`
        );
        const data = (await res.json().catch(() => ({}))) as {
          profile?: Profile | null;
          cvUploads?: CvUpload[];
        };
        console.log('[HomePage] /profile response', res.status, JSON.stringify(data));
        if (res.ok) {
          let p = data.profile ?? null;

          // Auto-upsert on first login: if no DB row yet, write Clerk data immediately
          // so we always have a row to update (e.g. for linkedin_eea_eligible).
          if (!p) {
            const linkedInAccount = user.externalAccounts?.find(acc =>
              acc.provider.toLowerCase().includes('linkedin')
            );
            // LinkedIn OIDC does not return a profile URL — only name, email, picture, locale.
            // We store what Clerk gives us; the user can enter their LinkedIn URL manually.
            console.log('[HomePage] no profile row — auto-upserting from Clerk data', {
              clerkUserId: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              firstName: user.firstName ?? linkedInAccount?.firstName,
              lastName: user.lastName ?? linkedInAccount?.lastName,
            });
            const upsertRes = await fetch(`${apiBaseUrl}/profile/upsert`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                clerkUserId: user.id,
                email: user.primaryEmailAddress?.emailAddress ?? linkedInAccount?.emailAddress ?? '',
                firstName: user.firstName ?? linkedInAccount?.firstName ?? null,
                lastName: user.lastName ?? linkedInAccount?.lastName ?? null,
                profilePictureUrl: user.imageUrl ?? linkedInAccount?.imageUrl ?? null,
              }),
            });
            const upsertData = (await upsertRes.json().catch(() => ({}))) as {
              profile?: Profile | null;
              cvUploads?: CvUpload[];
              error?: string;
            };
            console.log('[HomePage] auto-upsert response', upsertRes.status, JSON.stringify(upsertData));
            if (upsertRes.ok && upsertData.profile) {
              p = upsertData.profile;
            }
          }

          setProfile(p);
          const primaryCv = data.cvUploads?.find(cv => cv.is_primary === 1);
          setExistingCv(primaryCv ?? data.cvUploads?.[0] ?? null);
          if (p?.linkedin_connected === 1) {
            setIsLinkedinConnected(true);
          }

          // Initialise editable profile form from what we already know.
          setProfileForm({
            firstName: p?.first_name ?? user.firstName ?? '',
            lastName: p?.last_name ?? user.lastName ?? '',
            email: p?.email ?? user.primaryEmailAddress?.emailAddress ?? '',
            linkedinUrl: p?.linkedin_url ?? '',
          });

          // Reflect already-known non-EEA status on the LinkedIn slide.
          if (p?.linkedin_eea_eligible === 0) {
            setLinkedinNotice('not_eea');
          }
        } else {
          console.error('[HomePage] /profile fetch failed', res.status);
        }
      } catch (err) {
        console.error('[HomePage] /profile fetch threw', err);
      }
      setIsLoading(false);
    };

    load();
  }, [apiBaseUrl, isLoaded, user?.id]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleConnectLinkedIn = async () => {
    if (!user?.id) return;
    setIsConnectingLinkedIn(true);
    setLinkedinNotice(null);

    try {
      const returnTo = `${window.location.origin}/`;
      const qs = new URLSearchParams({
        clerkUserId: user.id,
        email: user.primaryEmailAddress?.emailAddress ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        returnTo,
      });
      console.log('[HomePage] calling /linkedin/auth/start with', Object.fromEntries(qs));
      const res = await fetch(`${apiBaseUrl}/linkedin/auth/start?${qs.toString()}`);
      const data = (await res.json().catch(() => ({}))) as {
        authUrl?: string;
        error?: string;
      };
      console.log('[HomePage] /linkedin/auth/start response', res.status, JSON.stringify(data));

      if (res.ok && data.authUrl) {
        console.log('[HomePage] redirecting to LinkedIn OAuth...');
        window.location.href = data.authUrl;
      } else {
        console.error('[HomePage] /linkedin/auth/start failed:', data.error);
        setLinkedinNotice('error');
        setIsConnectingLinkedIn(false);
      }
    } catch (err) {
      console.error('[HomePage] /linkedin/auth/start threw', err);
      setLinkedinNotice('error');
      setIsConnectingLinkedIn(false);
    }
  };

  const handleFile = (file: File) => {
    if (ACCEPTED_MIME.includes(file.type)) {
      setCvFile(file);
      setCvSaveError('');
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleCvNext = async () => {
    if (!cvFile) {
      if (existingCv) {
        setStep(2);
        return;
      }
      setCvSaveError('Please select a CV file before continuing.');
      return;
    }

    if (!user?.id) return;

    setIsSavingCv(true);
    setCvSaveError('');

    try {
      const formData = new FormData();
      formData.append('clerkUserId', user.id);
      formData.append('email', user.primaryEmailAddress?.emailAddress ?? '');
      formData.append('firstName', user.firstName ?? '');
      formData.append('lastName', user.lastName ?? '');
      formData.append('file', cvFile);

      console.log('[HomePage] uploading CV for user', user.id, 'file:', cvFile.name, cvFile.type, cvFile.size);
      const res = await fetch(`${apiBaseUrl}/cv/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        upload?: CvUpload;
      };
      console.log('[HomePage] /cv/upload response', res.status, JSON.stringify(data));

      if (!res.ok) throw new Error(data.error ?? 'Upload failed.');

      if (data.upload) setExistingCv(data.upload);
      setCvFile(null);
      setStep(2);
    } catch (err) {
      setCvSaveError(String(err));
    } finally {
      setIsSavingCv(false);
    }
  };

  const handleApply = async () => {
    if (!user?.id) return;

    setIsApplying(true);
    setApplyError('');

    try {
      console.log('[HomePage] handleApply: upserting profile for user', user.id);
      // Persist the user-edited profile form values before submitting.
      const upsertRes = await fetch(`${apiBaseUrl}/profile/upsert`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          clerkUserId: user.id,
          email: profileForm.email || (user.primaryEmailAddress?.emailAddress ?? ''),
          firstName: profileForm.firstName || (user.firstName ?? null),
          lastName: profileForm.lastName || (user.lastName ?? null),
          linkedinUrl: profileForm.linkedinUrl || null,
          profilePictureUrl: user.imageUrl ?? null,
        }),
      });

      console.log('[HomePage] profile upsert status:', upsertRes.status);
      const res = await fetch(`${apiBaseUrl}/application/submit`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ clerkUserId: user.id }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        profile?: Profile;
      };
      console.log('[HomePage] /application/submit response', res.status, JSON.stringify(data));

      if (!res.ok) throw new Error(data.error ?? 'Failed to submit application.');

      setProfile(data.profile ?? { ...profile!, application_status: 'pending' });
    } catch (err) {
      setApplyError(String(err));
    } finally {
      setIsApplying(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box sx={{ pt: 10, display: 'flex', justifyContent: 'center' }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  if (profile?.application_status) {
    return <ProfileView profile={profile} cv={existingCv} user={user as ClerkUser} />;
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 65px)',
        backgroundColor: 'grey.50',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: { xs: 4, md: 8 },
        pb: 8,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        {/* Step progress */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
            Step {step + 1} of 3
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['LinkedIn', 'Upload CV', 'Profile'].map((label, i) => (
              <Box key={label} sx={{ flex: 1 }}>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: i <= step ? 'primary.main' : 'grey.200',
                    transition: 'background-color 0.3s',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: i <= step ? 'primary.main' : 'text.disabled',
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {step === 0 && (
          <LinkedInSlide
            isConnected={isLinkedinConnected}
            notice={linkedinNotice}
            isConnecting={isConnectingLinkedIn}
            onConnect={handleConnectLinkedIn}
            onNext={() => setStep(1)}
          />
        )}
        {step === 1 && (
          <CvSlide
            cvFile={cvFile}
            existingCv={existingCv}
            isDragging={isDragging}
            isSaving={isSavingCv}
            saveError={cvSaveError}
            fileInputRef={fileInputRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileInput={handleFileInput}
            onClear={() => {
              setCvFile(null);
              setCvSaveError('');
            }}
            onPrev={() => setStep(0)}
            onNext={handleCvNext}
          />
        )}
        {step === 2 && (
          <ProfileSlide
            profile={profile}
            profileForm={profileForm}
            onFormChange={(field, value) =>
              setProfileForm(prev => ({ ...prev, [field]: value }))
            }
            user={user as ClerkUser}
            isLinkedinConnected={isLinkedinConnected}
            cv={existingCv}
            isApplying={isApplying}
            applyError={applyError}
            onPrev={() => setStep(1)}
            onApply={handleApply}
          />
        )}
      </Box>
    </Box>
  );
}

// ─── Step 1 — LinkedIn ────────────────────────────────────────────────────────

function LinkedInSlide({
  isConnected,
  notice,
  isConnecting,
  onConnect,
  onNext,
}: {
  isConnected: boolean;
  notice: 'connected' | 'error' | 'not_eea' | null;
  isConnecting: boolean;
  onConnect: () => void;
  onNext: () => void;
}) {
  const isDisabled = isConnecting || notice === 'not_eea';

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        p: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <LinkedInIcon sx={{ fontSize: 48, color: '#0A66C2', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Connect your LinkedIn
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Share your LinkedIn data with us to help match you with the right opportunities in the
          Brainpool.
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
          This is an advanced feature available only to profiles based in the European Economic
          Area (EEA).
        </Typography>
      </Box>

      {isConnected ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            border: '1px solid',
            borderColor: 'success.light',
            borderRadius: 1,
            backgroundColor: 'rgba(119, 201, 184, 0.08)',
            mb: 3,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
            LinkedIn connected successfully
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            component="button"
            onClick={isDisabled ? undefined : onConnect}
            disabled={isDisabled}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1.5,
              px: 3,
              mb: notice === 'not_eea' ? 1 : 3,
              border: 'none',
              borderRadius: 1,
              backgroundColor: isDisabled ? 'grey.300' : '#0A66C2',
              color: isDisabled ? 'text.disabled' : '#fff',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              fontSize: '0.9375rem',
              fontWeight: 600,
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: isDisabled ? 'grey.300' : '#004182' },
            }}
          >
            <LinkedInIcon sx={{ fontSize: 20 }} />
            {isConnecting ? 'Redirecting to LinkedIn…' : 'Connect LinkedIn'}
          </Box>

          {notice === 'not_eea' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                border: '1px solid',
                borderColor: 'error.light',
                borderRadius: 1,
                backgroundColor: 'rgba(211,47,47,0.04)',
                mb: 3,
              }}
            >
              <CancelIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                Your profile is based outside the EEA — LinkedIn data sharing is not available
                for your account. You can still apply by uploading your CV.
              </Typography>
            </Box>
          )}

          {notice === 'error' && (
            <Typography variant="body2" sx={{ color: 'error.main', mb: 3, textAlign: 'center' }}>
              Connection failed. Please try again.
            </Typography>
          )}
        </>
      )}

      <Box
        sx={{ display: 'flex', justifyContent: isConnected ? 'flex-end' : 'space-between' }}
      >
        {!isConnected && (
          <Button variant="text" onClick={onNext} sx={{ color: 'text.secondary' }}>
            Skip for now
          </Button>
        )}
        <Button variant="contained" onClick={onNext}>
          {isConnected ? 'Continue →' : 'Next →'}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Step 2 — CV Upload ───────────────────────────────────────────────────────

function CvSlide({
  cvFile,
  existingCv,
  isDragging,
  isSaving,
  saveError,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  onClear,
  onPrev,
  onNext,
}: {
  cvFile: File | null;
  existingCv: CvUpload | null;
  isDragging: boolean;
  isSaving: boolean;
  saveError: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        p: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Upload your CV
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Upload your CV or résumé — PDF, DOC, or DOCX, up to 10 MB.
        </Typography>
      </Box>

      {cvFile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 1,
            backgroundColor: 'rgba(119, 201, 184, 0.08)',
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
              {cvFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {(cvFile.size / 1024).toFixed(0)} KB · selected for upload
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={onClear}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              p: 0.5,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <CloseIcon fontSize="small" />
          </Box>
        </Box>
      ) : existingCv ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 1,
              backgroundColor: 'rgba(119, 201, 184, 0.08)',
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
                {existingCv.original_filename}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {(existingCv.size_bytes / 1024).toFixed(0)} KB · currently uploaded
              </Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={() => fileInputRef.current?.click()}
            >
              Replace
            </Button>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            style={{ display: 'none' }}
            onChange={onFileInput}
          />
        </>
      ) : (
        <>
          <Box
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              backgroundColor: isDragging ? 'rgba(119,201,184,0.06)' : 'transparent',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(119,201,184,0.04)',
              },
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Drag & drop your CV here, or{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                browse
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              PDF, DOC, or DOCX · max 10 MB
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            style={{ display: 'none' }}
            onChange={onFileInput}
          />
        </>
      )}

      {saveError && (
        <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>
          {saveError}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onPrev} disabled={isSaving}>
          ← Back
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={isSaving || (!cvFile && !existingCv)}
        >
          {isSaving ? 'Uploading…' : 'Next →'}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Step 3 — Profile review & submit ────────────────────────────────────────

function ProfileSlide({
  profile,
  profileForm,
  onFormChange,
  user,
  isLinkedinConnected,
  cv,
  isApplying,
  applyError,
  onPrev,
  onApply,
}: {
  profile: Profile | null;
  profileForm: { firstName: string; lastName: string; email: string; linkedinUrl: string };
  onFormChange: (field: string, value: string) => void;
  user: ClerkUser;
  isLinkedinConnected: boolean;
  cv: CvUpload | null;
  isApplying: boolean;
  applyError: string;
  onPrev: () => void;
  onApply: () => void;
}) {
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        p: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Your Profile
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Review and complete the information below, then submit your application.
        </Typography>
      </Box>

      {/* Personal info — editable */}
      <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1.5 }}>
        Personal Information
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="First name"
            size="small"
            fullWidth
            value={profileForm.firstName}
            onChange={e => onFormChange('firstName', e.target.value)}
            disabled={isApplying}
          />
          <TextField
            label="Last name"
            size="small"
            fullWidth
            value={profileForm.lastName}
            onChange={e => onFormChange('lastName', e.target.value)}
            disabled={isApplying}
          />
        </Box>
        <TextField
          label="Email"
          size="small"
          fullWidth
          value={profileForm.email}
          onChange={e => onFormChange('email', e.target.value)}
          disabled={isApplying}
        />
        <TextField
          label="LinkedIn profile URL"
          size="small"
          fullWidth
          placeholder="https://linkedin.com/in/your-profile"
          value={profileForm.linkedinUrl}
          onChange={e => onFormChange('linkedinUrl', e.target.value)}
          disabled={isApplying || isLinkedinConnected}
          helperText={
            isLinkedinConnected
              ? 'Populated from your connected LinkedIn account'
              : 'Optional — paste your public LinkedIn profile URL'
          }
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* CV & LinkedIn status — read-only summary */}
      <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1.5 }}>
        Documents & Connections
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <SummaryRow
          label="CV"
          value={cv ? cv.original_filename : 'Not uploaded'}
          positive={!!cv}
        />
        <SummaryRow
          label="LinkedIn"
          value={isLinkedinConnected ? 'Connected ✓' : 'Not connected'}
          positive={isLinkedinConnected}
        />
      </Stack>

      {/* Placeholder notice for future CV extraction */}
      <Box
        sx={{
          p: 1.5,
          border: '1px dashed',
          borderColor: 'grey.300',
          borderRadius: 1,
          backgroundColor: 'grey.50',
          mb: 3,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Additional skills and experience will be extracted automatically from your CV once
          processing is complete.
        </Typography>
      </Box>

      {applyError && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
          {applyError}
        </Typography>
      )}

      {!cv && (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mb: 2 }}
        >
          Please go back and upload a CV to continue.
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={onPrev} disabled={isApplying}>
          ← Back
        </Button>
        <Button variant="contained" onClick={onApply} disabled={isApplying || !cv}>
          {isApplying ? 'Submitting…' : 'Submit Application'}
        </Button>
      </Box>
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  positive,
}: {
  label: string;
  value: string;
  positive?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: positive === false ? 'text.disabled' : 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ─── Profile view (post-application) ─────────────────────────────────────────

const STATUS_CONFIG: Record<
  ApplicationStatus,
  {
    label: string;
    color: 'warning' | 'success' | 'error';
    Icon: React.ComponentType<{ sx?: object }>;
    message: string;
  }
> = {
  pending: {
    label: 'Pending Review',
    color: 'warning',
    Icon: HourglassEmptyIcon,
    message: "We've received your application and will be in touch soon.",
  },
  approved: {
    label: 'Approved',
    color: 'success',
    Icon: CheckCircleIcon,
    message: 'Congratulations! You have been accepted into the Brainpool.',
  },
  rejected: {
    label: 'Not accepted',
    color: 'error',
    Icon: CancelIcon,
    message:
      "Unfortunately your application wasn't successful this time. You're welcome to update your profile and reapply.",
  },
};

function ProfileView({
  profile,
  cv,
  user,
}: {
  profile: Profile;
  cv: CvUpload | null;
  user: ClerkUser;
}) {
  const status = profile.application_status!;
  const { label, color, Icon, message } = STATUS_CONFIG[status];

  const displayName =
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    user.primaryEmailAddress?.emailAddress ||
    '—';

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 65px)',
        backgroundColor: 'grey.50',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: { xs: 4, md: 8 },
        pb: 8,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        {/* Status banner */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3,
            mb: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Icon sx={{ fontSize: 40, color: `${color}.main`, mt: 0.25 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Application Status
              </Typography>
              <Chip label={label} color={color} size="small" />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {message}
            </Typography>
          </Box>
        </Box>

        {/* Profile details */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Your Profile
          </Typography>

          <Stack spacing={0}>
            <ProfileRow label="Name" value={displayName} />
            <Divider />
            <ProfileRow label="Email" value={profile.email} />
            <Divider />
            <ProfileRow
              label="LinkedIn"
              value={profile.linkedin_connected ? 'Connected' : 'Not connected'}
            />
            <Divider />
            <ProfileRow
              label="CV"
              value={
                cv
                  ? `${cv.original_filename} (${(cv.size_bytes / 1024).toFixed(0)} KB)`
                  : 'Not uploaded'
              }
            />
            {profile.applied_at && (
              <>
                <Divider />
                <ProfileRow
                  label="Applied"
                  value={new Date(profile.applied_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 100 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: 'text.primary', textAlign: 'right' }}
      >
        {value}
      </Typography>
    </Box>
  );
}
